import React, { ChangeEvent, FormEvent, Reducer, useEffect, useReducer, useRef, useState } from 'react';
import SearchModal from '../../components/bearing-search/components/SearchModal';
import { text, unitList, unitMap } from '../data';
import BearingTypeAssistance from '../components/BearingTypeAssistance';
import bearingSearchReducer, {
	BearingAdvancedFilter,
	BearingSearchAction,
	BearingSearchState,
	initialBearingSearchState,
} from './reducer';
import BearingTypeDropdown from '../components/bearing-type-dropdown/BearingTypeDropdown';
import { bearingGroups, getBearings } from '../../components/bearing-search/api/api';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import {
	BearingAdvancedFilterKey,
	BearingInfo,
	BearingPayload,
	BearingResponse,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import NumberInput from '../../components/bearing-search/components/forms/NumberInput';
import BearingSearchResults from '../components/bearing-search-results/BearingSearchResults';
import {
	trackStart,
	updateActionParams,
	trackFilters,
	trackProductResults,
} from '../utils/analytics';
import {
	toggleNextTools,
	getPartStateFromParams,
	getMarketPlaceDescriptionFromUrl
} from '../utils/engineering-tools';
import { BearingGroupOption } from '../types/types';
import PartPreSearch from '../components/PartPreSearch';

declare global {
	interface Window {
		searchTerm?: string;
	}
}

const BearingSearch: React.FC = () => {
	const [ isSearched, setIsSearched ] = useState( false );
	const [ isValidated, setIsValidated ] = useState( false );
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ displayUnit, setDisplayUnit ] = useState<TimkenUnit>( 'M' );
	const [ results, setResults ] = useState<BearingInfo[]>( [] );
	const [ totalRecords, setTotalRecords ] = useState( 0 );
	const [ isAssistanceModalOpen, setIsAssistanceModalOpen ] = useState( false );
	const [ isAdvancedFilterOpen, setIsAdvancedFilterOpen ] = useState( false );
	const [ preselectedTypes, setPreselectedTypes ] = useState<string[]>( [] );
	const [ formState, dispatch ] = useReducer<Reducer<BearingSearchState, BearingSearchAction>>(
		bearingSearchReducer,
		initialBearingSearchState,
	);
	const [ prevFormState, setPrevFormState ] = useState(initialBearingSearchState);
	const [ isRendered, setIsRendered ] = useState( false );
	const formRef = useRef<HTMLFormElement>( null );
	const [ bearingTypesGroups, setBearingTypesGroups ] = useState<BearingGroupOption[]>( [] );

	useEffect( () => {
		trackStart( 'bearingSearch' );

		const handleAccountInfoLoaded = ( ) => {
			if ( window.searchTerm ) {
				dispatch( {
					type: 'change_part_number',
					payload: window.searchTerm,
				} );

				dispatch( {
					type: 'update_request_data',
				} );

				setIsRendered( true );
			} else {
				const queryMap = new URLSearchParams(document.location.search);
				const savedPart = getPartStateFromParams(queryMap);

				if (queryMap.get('savedSearch') || queryMap.get('gig_savedSearch')) {
					// Restore from saved search
					const savedSearch = window.localStorage.getItem('savedSearch');

					if (savedSearch) {
						const savedState: BearingSearchState = JSON.parse(savedSearch);
						setPreselectedTypes(savedState.partType || []);

						dispatch({
							type: 'reset',
							payload: savedState,
						});

						dispatch({
							type: 'update_request_data',
						});

						setIsRendered(true);

						// This means that at least one of the advanced filters has a saved value
						if (savedSearch.includes('value')) {
							setIsAdvancedFilterOpen(true);
						}
					}
				} else if (savedPart.marketPlaceDescription) {
					dispatch( {
						type: 'change_part_number',
						payload: savedPart.marketPlaceDescription,
					} );

					dispatch({
						type: 'update_request_data',
					});

					setIsRendered( true );
				} else {
					// Clear saved search and start fresh
					window.localStorage.removeItem( 'savedSearch' );
				}
			}
		};

		document.addEventListener( 'timken_sso_account_info_loaded', handleAccountInfoLoaded );

		return () => {
			document.removeEventListener( 'timken_sso_account_info_loaded', handleAccountInfoLoaded );
		};
	}, [] );

	useEffect( () => {
		if ( ! isRendered ) {
			return;
		}

		formRef.current?.dispatchEvent(
			new Event( 'submit', { cancelable: true, bubbles: true } ),
		);
	}, [ isRendered ] );

	useEffect( () => {
		const isShown = isSearched && ! isResultsLoading;

		toggleNextTools( isShown );

		return () => toggleNextTools( false );
	}, [ isSearched, isResultsLoading ] );

	const selectTypesFromAssistance = ( partTypes: string[] ) => {
		// select part types;
		setPreselectedTypes( partTypes );

		// clear assistance modal status
		setIsAssistanceModalOpen( false );
	};

	const selectTypes = ( typeData: { partType: string[]; bearingType: string[]; bearingSubType: string[]; } ) => {
		setIsValidated( false );

		dispatch( {
			type: 'change_bearing_type',
			payload: typeData,
		} );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const refineResults = ( payload: Partial<BearingPayload> ) => {
		const request: Partial<BearingPayload> = {
			...prevFormState.requestData,
			...payload,
		};

		getBearings( request ).then( ( response: BearingResponse ) => {
			setResults( response.bearingInformation || [] );
			setTotalRecords( response.totalRecords );
		} );
	};

	const handleFormChange = () => {
		setIsValidated( false );

		dispatch( {
			type: 'update_request_data',
		} );
	};

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();
		setIsValidated( true );

		if ( formState.requestData ) {
			setPrevFormState( formState );
			setIsSearched( true );
			setIsResultsLoading( true );
			setResults( [] );

			const payload: Partial<BearingPayload> = {
				...formState.requestData,
				pageNumber: 1,
			};

			trackFilters( payload, bearingTypesGroups );

			getBearings( payload )
				.then( ( response: BearingResponse ) => {
					trackProductResults( 'bearingSearch', payload, response.totalRecords, formState.marketPlaceDescription );
					updateActionParams( 'bearingSearch' );
					setDisplayUnit( formState.unit );
					setResults( response.bearingInformation || [] );
					setTotalRecords( response.totalRecords );
					// Save search state
					window.localStorage.setItem( 'savedSearch', JSON.stringify( formState ) );

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth',
					});
				} )
				.finally( () => {
					setIsResultsLoading( false );
				} );
		}
	};

	const clearSearch = () => {
		setIsSearched( false );
		setIsValidated( false );

		// Reset form
		dispatch( {
			type: 'reset',
		} );

		// Reset bearing type dropdown
		setPreselectedTypes( [] );

		// Remove search state
		window.localStorage.removeItem( 'savedSearch' );
	};
	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};
	const setPartFromPresearch = (input: string) => {
		dispatch( {
			type: 'change_part_number',
			payload: input,
		} );
		dispatch( {
			type: 'update_request_data',
		} );
	};

	useEffect(() => {
		const marketPlaceDescriptionFromUrl = getMarketPlaceDescriptionFromUrl();
		if (marketPlaceDescriptionFromUrl) {
			dispatch( {
				type: 'change_part_number',
				payload: marketPlaceDescriptionFromUrl,
			} );
			dispatch( {
				type: 'update_request_data',
			} );
			setIsPartSelected( true );
		}
	}, []);

	return (
		<div>
			<button
				className="btn btn--tertiary t-engineering-tools__help-me-choose"
				aria-controls="help_me_choose_modal"
				onClick={() => setIsAssistanceModalOpen( true )}
			>
				{text.helpMeChoose}
			</button>

			<form onSubmit={handleSubmit} onChange={handleFormChange} ref={formRef}>
				<div className="row">
					<div className="col-xl-8">
						<div className="row form-row">
							<div className="col-md-4 col-xl-5">
								<BearingTypeDropdown
									id="bearing_search_type"
									label={text.bearingType}
									placeholder={text.selectBearingType}
									groups={bearingGroups}
									preselected={preselectedTypes}
									onChange={selectTypes}
									onApply={( groups ) => {
										setBearingTypesGroups( groups )

										let selectedType = [];
										groups.forEach(function (group) {

											group.types.forEach(function (type) {
												if (type.selected === true) {
													selectedType.push(type.value);
												}
											});
										})

										setPreselectedTypes(selectedType)

									}}
								/>
							</div>

							<div className="col-md-5 col-xl-5 timken-tool-presearch-container">
								<TextInput
									id="bearing_search_part_number"
									label={text.partNumber}
									name="marketPlaceDescription"
									value={formState.marketPlaceDescription}
									placeholder={text.partNumber}
									message={text.partWildCardHint}
									onChange={( event: ChangeEvent<HTMLInputElement> ) => {
										dispatch( {
											type: 'change_part_number',
											payload: event.target.value as string,
										} );
										setIsPartSelected( false );
									}
									}
								/>
								<PartPreSearch
									input={formState.marketPlaceDescription}
									isHidden={formState.marketPlaceDescription.length < 2 || (isSearched && isResultsLoading) || isPartSelected}
									unit={formState.unit}
									onClick={preSearchClickHandler}
									verifyPartNumber={(value: string) => setPartFromPresearch(value)}
								/>
							</div>

							<div className="col-md-3 col-xl-2">
								<SelectDropdown
									id="bearing_search_unit"
									required={true}
									label={text.displayUnits}
									value={formState.unit}
									options={unitList}
									onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
										dispatch( {
											type: 'change_unit',
											payload: event.target.value as TimkenUnit,
										} )
									}
								/>
							</div>
						</div>
					</div>

					<div className="col-xl-4 text-xl-end">
						<button
							type="button"
							className="btn btn--secondary t-engineering-tools__advanced-filter-toggle"
							aria-controls="bearing_search_advanced-filters"
							aria-expanded={isAdvancedFilterOpen}
							onClick={() => setIsAdvancedFilterOpen( ! isAdvancedFilterOpen )}
						>
							<span className="btn__icon icon-filter" aria-hidden="true"></span>
							<span>{isAdvancedFilterOpen ? text.hideAdvancedFilters : text.advancedFilters}</span>
						</button>
					</div>
				</div>

				<div
					className={`t-engineering-tools__constraints ${ isAdvancedFilterOpen ? '' : 'd-none' }`}
					id="bearing_search_advanced-filters"
				>
					<h3 className="advanced_filter_margin">{text.advancedFilters}</h3>

					<div className="row form-row">
						{Object.keys( formState.advancedFilters ).map( ( key ) => {
							const filter = formState.advancedFilters[ key ];

							if ( ! filter ) {
								return null;
							}

							const { id, label, value, type, isApplicable, tooltip } = filter as BearingAdvancedFilter;
							const validation = formState.validation.get( key as BearingAdvancedFilterKey );
							const unitLabel = unitMap[ formState.unit ][ type ];

							return (
								<div className="col-md-3 col-xl-2" key={id}>
									<NumberInput
										id={id}
										label={label}
										unitLabel={unitLabel}
										tooltip={tooltip}
										value={value}
										min={0}
										disabled={! isApplicable}
										invalid={isValidated && validation?.invalid}
										errorMessage={validation?.message}
										onChange={( event: ChangeEvent<HTMLInputElement> ) =>
											dispatch( {
												type: 'change_number_field',
												payload: [ key as keyof BearingPayload, event.target.value ],
											} )
										}
									/>
								</div>
							);
						} )}
					</div>
				</div>

				<div className="t-engineering-tools__search-actions">
					<button className="btn btn--primary" type="submit">
						{text.search}
					</button>

					<button className="btn btn--secondary" type="button" onClick={() => clearSearch()}>
						{text.clear}
					</button>
				</div>
			</form>

			{isSearched && (
				<BearingSearchResults
					loading={isResultsLoading}
					unit={displayUnit}
					data={results}
					totalRecords={totalRecords}
					onRefine={refineResults}
				/>
			)}

			<SearchModal
				id="help_me_choose_modal"
				isOpen={isAssistanceModalOpen}
				onClose={() => setIsAssistanceModalOpen( false )}
			>
				<BearingTypeAssistance preselected={preselectedTypes} isOpen={isAssistanceModalOpen} onApply={selectTypesFromAssistance} onCancel={() => setIsAssistanceModalOpen( false )} />
			</SearchModal>
		</div>
	);
};

export default BearingSearch;
